// Typed client for the portfolio backend (FastAPI). All admin writes attach the
// JWT stored by lib/auth; public GETs are unauthenticated.

// VITE_API_URL is inlined at BUILD time. If the deploy platform doesn't set it,
// falling back to localhost ships a bundle that asks the *visitor's* machine for
// the API — every request fails and the site silently renders its static
// fallbacks. So only use the localhost default during local dev; in a
// production build a missing value is a deploy misconfiguration we surface
// loudly rather than paper over.
const CONFIGURED_API_URL = (import.meta.env.VITE_API_URL ?? '').trim();

if (import.meta.env.PROD && !CONFIGURED_API_URL) {
  console.error(
    '[api] VITE_API_URL is not set for this production build. Set it in your ' +
    'hosting provider\'s environment variables and redeploy — the site will ' +
    'otherwise show only fallback content.',
  );
}

const BASE = (CONFIGURED_API_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');

const TOKEN_KEY = 'admin_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';

interface ReqOpts {
  auth?: boolean;
  totp?: string;      // fresh 6-digit code for step-up endpoints
  raw?: boolean;      // body is already a BodyInit (e.g. FormData)
  credentials?: boolean; // send the httpOnly refresh cookie (auth endpoints)
}

async function request<T>(method: Method, path: string, body?: unknown, opts: ReqOpts = {}): Promise<T> {
  const headers: Record<string, string> = {};
  const isRaw = opts.raw === true;
  if (body !== undefined && !isRaw) headers['Content-Type'] = 'application/json';
  if (opts.auth) {
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  if (opts.totp) headers['X-TOTP-Code'] = opts.totp;

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    credentials: opts.credentials ? 'include' : 'same-origin',
    body: body === undefined ? undefined : isRaw ? (body as BodyInit) : JSON.stringify(body),
  });

  if (!res.ok) {
    let detail = res.statusText;
    try {
      const data = await res.json();
      detail = typeof data?.detail === 'string' ? data.detail : JSON.stringify(data?.detail ?? data);
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, detail);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ── Entity types (mirror the backend schemas) ──

export interface ExperienceProject {
  name: string;
  description: string;
}

export interface Experience {
  id: string;
  slug: string;
  year: string;
  title: string;
  role: string;
  logo?: string | null;
  image?: string | null;
  location?: string | null;
  industry?: string | null;
  website?: string | null;
  description?: string | null;
  projects: ExperienceProject[];
  sort_order: number;
  created_at: string;
  updated_at: string;
}
export type ExperienceInput = Omit<Experience, 'id' | 'created_at' | 'updated_at'>;

export interface Work {
  id: string;
  slug: string;
  title: string;
  role: string;
  description?: string | null;
  bullets: string[];
  tags: string[];
  link?: string | null;
  image?: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}
export type WorkInput = Omit<Work, 'id' | 'created_at' | 'updated_at'>;

export interface Blog {
  id: string;
  slug: string;
  title: string;
  excerpt?: string | null;
  tags: string[];
  reading?: string | null;
  category?: string | null;
  image?: string | null;
  content: string;
  date_published?: string | null;
  published: boolean;
  created_at: string;
  updated_at: string;
}
export type BlogInput = Omit<Blog, 'id' | 'created_at' | 'updated_at'>;

export interface SiteSettings {
  display_name: string;
  tagline: string;
  location: string;
  site_title: string;
  site_description: string;
  open_to_work: boolean;
  show_blog_count: boolean;
  maintenance_mode: boolean;
  maintenance_message: string;
  resume_url: string | null;
}

export interface About {
  content: string;
  photo: string | null;
}

/** Visitor geo resolved at the edge by the Cloudflare Worker. */
export interface VisitorInfo {
  ip: string | null;
  country: string | null;
  city: string | null;
  region: string | null;
  timezone: string | null;
  isp: string | null;
}

export interface PageViewPayload extends Partial<VisitorInfo> {
  path: string;
  referrer?: string;
  session_id?: string;
  duration_ms?: number;
}

/** One-line chat invitations per section, generated from live content. */
export type SectionTaglines = Record<string, string>;

export interface CacheSnapshot {
  /** Present only when the snapshot is served via the Worker. */
  visitor?: VisitorInfo;
  experiences: Experience[];
  works: Work[];
  blogs: Blog[];
  about: About;
  settings: SiteSettings;
  /** Absent until the Worker has generated them; callers fall back locally. */
  taglines?: SectionTaglines;
}

export interface SitemapResult {
  sitemap: { pages: number; blogs: number; experience: number; works: number; generated_at: string };
  pings: Record<string, string>;
  llms?: { bytes: number; path: string };
}

export interface ContactInput {
  name: string;
  email: string;
  message: string;
}

/** Absolute URL for the uploaded resume (BASE + relative path), or null. */
export function resolveUrl(path: string | null | undefined): string | null {
  return path ? `${BASE}${path}` : null;
}

export interface GeoPoint {
  ip: string | null;
  city: string | null;
  region: string | null;
  country: string | null;
  views: number;
  unique_visitors: number;
  last_seen: string;
}

export interface VisitorLog {
  id: string;
  path: string;
  ip: string | null;
  city: string | null;
  region: string | null;
  country: string | null;
  timezone: string | null;
  isp: string | null;
  referrer: string | null;
  user_agent: string | null;
  session_id: string | null;
  duration_ms: number | null;
  created_at: string;
}

export interface ChatMessageLog {
  id: string;
  session_id: string | null;
  role: 'user' | 'assistant';
  content: string;
  ip: string | null;
  country: string | null;
  city: string | null;
  created_at: string;
}

export interface AnalyticsSummary {
  total_views: number;
  unique_visitors: number;
  unique_ips: number;
  countries: number;
  avg_session_ms: number;
  resume_downloads: number;
  top_pages: { path: string; views: number }[];
  stats: { label: string; value: string; change?: string | null; trend?: string | null }[];
  geo: GeoPoint[];
}

export interface VisitFilter {
  days?: number;
  ip?: string;
  country?: string;
  city?: string;
  limit?: number;
}

// ── API surface ──

export const api = {
  // Auth: exchange email + current TOTP code for a short access token + refresh
  // cookie. credentials:true so the browser stores the httpOnly refresh cookie.
  login: (email: string, code: string) =>
    request<{ access_token: string; token_type: string; expires_in: number }>(
      'POST', '/auth/login', { email, code }, { credentials: true },
    ),
  // Rotate a fresh access token from the refresh cookie (called while active).
  refresh: () =>
    request<{ access_token: string; token_type: string; expires_in: number }>(
      'POST', '/auth/refresh', undefined, { credentials: true },
    ),
  // Clear the refresh cookie server-side.
  logout: () => request<void>('POST', '/auth/logout', undefined, { credentials: true }),

  experiences: {
    list: () => request<Experience[]>('GET', '/experiences'),
    get: (slug: string) => request<Experience>('GET', `/experiences/${slug}`),
    create: (data: Partial<ExperienceInput>) => request<Experience>('POST', '/experiences', data, { auth: true }),
    update: (id: string, data: Partial<ExperienceInput>) =>
      request<Experience>('PUT', `/experiences/${id}`, data, { auth: true }),
    remove: (id: string) => request<void>('DELETE', `/experiences/${id}`, undefined, { auth: true }),
    uploadLogo: (id: string, file: File) => {
      const form = new FormData();
      form.append('file', file);
      return request<Experience>('POST', `/experiences/${id}/logo`, form, { auth: true, raw: true });
    },
    uploadImage: (id: string, file: File) => {
      const form = new FormData();
      form.append('file', file);
      return request<Experience>('POST', `/experiences/${id}/image`, form, { auth: true, raw: true });
    },
  },

  works: {
    list: () => request<Work[]>('GET', '/works'),
    get: (slug: string) => request<Work>('GET', `/works/${slug}`),
    create: (data: Partial<WorkInput>) => request<Work>('POST', '/works', data, { auth: true }),
    update: (id: string, data: Partial<WorkInput>) => request<Work>('PUT', `/works/${id}`, data, { auth: true }),
    remove: (id: string) => request<void>('DELETE', `/works/${id}`, undefined, { auth: true }),
    uploadImage: (id: string, file: File) => {
      const form = new FormData();
      form.append('file', file);
      return request<Work>('POST', `/works/${id}/image`, form, { auth: true, raw: true });
    },
  },

  blogs: {
    list: () => request<Blog[]>('GET', '/blogs'),
    get: (slug: string) => request<Blog>('GET', `/blogs/${slug}`),
    create: (data: Partial<BlogInput>) => request<Blog>('POST', '/blogs', data, { auth: true }),
    update: (id: string, data: Partial<BlogInput>) => request<Blog>('PUT', `/blogs/${id}`, data, { auth: true }),
    remove: (id: string) => request<void>('DELETE', `/blogs/${id}`, undefined, { auth: true }),
    // Upload a cover image for the blog post itself.
    uploadImage: (id: string, file: File) => {
      const form = new FormData();
      form.append('file', file);
      return request<Blog>('POST', `/blogs/${id}/image`, form, { auth: true, raw: true });
    },
    // Upload an image/GIF for use inline in markdown. Returns the URL to insert.
    uploadMarkdownImage: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return request<{ url: string; content_type: string; size_bytes: number }>(
        'POST', '/blog-images', form, { auth: true, raw: true },
      );
    },
  },

  settings: {
    get: () => request<SiteSettings>('GET', '/settings'),
    // Impactful writes require a fresh TOTP code (step-up auth).
    update: (data: Partial<SiteSettings>, totp: string) =>
      request<SiteSettings>('PUT', '/settings', data, { auth: true, totp }),
    uploadResume: (file: File, totp: string) => {
      const form = new FormData();
      form.append('file', file);
      return request<SiteSettings>('POST', '/settings/resume', form, { auth: true, totp, raw: true });
    },
    removeResume: (totp: string) =>
      request<SiteSettings>('DELETE', '/settings/resume', undefined, { auth: true, totp }),
    regenerateSitemap: (totp: string) =>
      request<SitemapResult>('POST', '/settings/sitemap/regenerate', undefined, { auth: true, totp }),
  },

  about: {
    get: () => request<About>('GET', '/about'),
    update: (data: Partial<About>) => request<About>('PUT', '/about', data, { auth: true }),
    uploadPhoto: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return request<About>('POST', '/about/photo', form, { auth: true, raw: true });
    },
  },

  analytics: {
    summary: (days = 30) =>
      request<AnalyticsSummary>('GET', `/analytics/summary?days=${days}`, undefined, { auth: true }),
    collect: (view: PageViewPayload) => request<void>('POST', '/analytics/collect', view),
    chats: (filter: { days?: number; ip?: string; session_id?: string; limit?: number } = {}) => {
      const qs = new URLSearchParams(
        Object.entries(filter).flatMap(([k, v]) => (v == null || v === '' ? [] : [[k, String(v)]])),
      ).toString();
      return request<ChatMessageLog[]>('GET', `/analytics/chats${qs ? `?${qs}` : ''}`, undefined, { auth: true });
    },
    visits: (filter: VisitFilter = {}) => {
      const qs = new URLSearchParams(
        Object.entries(filter).flatMap(([k, v]) => (v == null || v === '' ? [] : [[k, String(v)]])),
      ).toString();
      return request<VisitorLog[]>('GET', `/analytics/visits${qs ? `?${qs}` : ''}`, undefined, { auth: true });
    },
  },

  contact: {
    send: (data: ContactInput) =>
      request<{ ok: boolean; message: string }>('POST', '/contact', data),
  },

  // Single aggregate read — everything the public site needs in one call.
  cache: {
    snapshot: () => request<CacheSnapshot>('GET', '/cache/snapshot'),
  },
};
